import {
  Home,
  Building2,
  Hammer,
  Construction,
  LandPlot,
  RefreshCcw,
  Landmark,
  BadgePercent,
  Clock3,
  Percent,
  FileCheck2,
  Users,
  Wallet,
  Briefcase,
  User,
  IndianRupee,
  CalendarDays,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),

  organizationSchema(),

  financialServiceSchema(),

  localBusinessSchema(),

  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Home Loan",
      path: "/home-loan",
    },
  ]),
];

export const seo = {
  title: "Home Loans",
  description:
    "Compare Home Loan offers from leading Banks and NBFCs. Get competitive interest rates, expert assistance, faster approvals and end-to-end support with Money Mart.",
  canonical: "/home-loan",
  structuredData: { schemas },
};

export const hero = {
  badge: "Trusted Home Loan Advisors",

  title: "Find the Right Home Loan for Your Dream Home",

  description:
    "Whether you're buying your first home, constructing a new house, renovating your property, or transferring an existing loan, Money Mart helps you compare Home Loan offers from leading Banks and NBFCs with expert guidance every step of the way.",

  image: "/images/loans/home_loan.webp",

  highlights: [
    "Competitive Interest Rates",
    "Fast Loan Approval",
    "Minimal Documentation",
    "Expert Loan Assistance",
  ],

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

export const overview = {
  badge: "Home Loan Solutions",

  title: "Everything You Need for Your Home Financing Journey",

  description:
    "Whether you're purchasing your first home, constructing a house, renovating an existing property, or refinancing an existing loan, Money Mart helps you find the right financing solution with expert guidance and access to leading banks and NBFCs.",

  items: [
    {
      icon: Home,
      title: "Purchase a Home",
      description:
        "Finance the purchase of a new or resale residential property with competitive interest rates.",
    },
    {
      icon: Hammer,
      title: "Construct Your House",
      description:
        "Build your dream home on your own plot with stage-wise loan disbursement.",
    },
    {
      icon: Landmark,
      title: "Renovate or Extend",
      description:
        "Upgrade, renovate, or expand your existing home without financial stress.",
    },
    {
      icon: RefreshCcw,
      title: "Transfer Existing Loan",
      description:
        "Move your existing Home Loan to a better lender and enjoy lower interest rates and improved terms.",
    },
  ],
};

export const benefits = {
  badge: "Why Choose a Home Loan",

  title: "Benefits of Taking a Home Loan Through Money Mart",

  description:
    "We help you compare loan offers from leading banks and NBFCs, ensuring you get competitive interest rates, transparent guidance, and a hassle-free borrowing experience.",

  items: [
    {
      icon: Percent,
      title: "Competitive Interest Rates",
      description: "Access attractive rates from multiple lenders.",
    },
    {
      icon: Clock3,
      title: "Quick Loan Approval",
      description: "Fast processing with minimal turnaround time.",
    },
    {
      icon: FileCheck2,
      title: "Simple Documentation",
      description: "Expert assistance to make paperwork hassle-free.",
    },
    {
      icon: Landmark,
      title: "Multiple Bank Options",
      description: "Compare offers from leading banks and NBFCs.",
    },
    {
      icon: Wallet,
      title: "Flexible Repayment",
      description: "Choose repayment tenures that suit your budget.",
    },
    {
      icon: Users,
      title: "Dedicated Relationship Manager",
      description: "Personal support throughout the loan journey.",
    },
  ],
};

export const types = {
  badge: "Loan Solutions",

  title: "Choose the Home Loan That Fits Your Requirement",

  description:
    "Whether you're buying your first home, constructing a house, renovating your property, or transferring an existing loan, Money Mart helps you find the right financing solution from India's leading banks and NBFCs.",

  items: [
    {
      icon: Home,
      title: "Home Purchase Loan",
      description:
        "Finance the purchase of a new or resale residential property with attractive interest rates and flexible repayment options.",
    },
    {
      icon: Hammer,
      title: "Home Construction Loan",
      description:
        "Build your dream house on your own plot with stage-wise loan disbursement based on construction progress.",
    },
    {
      icon: Building2,
      title: "Home Extension Loan",
      description:
        "Expand your existing home by adding new rooms or increasing living space without financial stress.",
    },
    {
      icon: Landmark,
      title: "Home Repair & Renovation Loan",
      description:
        "Upgrade or renovate your home with funding for repairs, remodeling, interiors, or modernization.",
    },
    {
      icon: Construction,
      title: "Under Construction Home",
      description:
        "Finance an under-construction home in a bank-approved project with stage-wise loan disbursement.",
    },
    {
      icon: LandPlot,
      title: "Plot Purchase + Construction",
      description:
        "Buy a residential plot and finance the construction of your home with a combined loan facility.",
    },
    {
      icon: RefreshCcw,
      title: "Balance Transfer",
      description:
        "Transfer your existing home loan to another lender offering lower interest rates and better service.",
    },
    {
      icon: BadgePercent,
      title: "Balance Transfer + Top-Up",
      description:
        "Transfer your existing loan and receive additional funds for renovation, furnishing, education, or other personal needs.",
    },
  ],
};

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Home Loan Eligibility",

  description:
    "Every lender has its own eligibility criteria. Our experts help maximize your loan eligibility by comparing multiple lenders and recommending the most suitable option.",

  requirements: [
    {
      icon: User,
      title: "Age",
      value: "21 – 70 Years",
    },
    {
      icon: Briefcase,
      title: "Employment",
      value: "Salaried or Self-Employed",
    },
    {
      icon: IndianRupee,
      title: "Stable Income",
      value: "Regular & verifiable income source",
    },
    {
      icon: CalendarDays,
      title: "Credit History",
      value: "Good CIBIL score improves approval chances",
    },
  ],

  approvalFactors: [
    "Monthly income and repayment capacity",
    "Existing EMIs and financial liabilities",
    "Applicant's age",
    "Employment stability",
    "Credit score and repayment history",
    "Property valuation and legal verification",
  ],

  cta: {
    title: "Not sure if you're eligible?",

    description:
      "Our loan experts will evaluate your profile, compare lenders, and suggest the best loan options with no obligation.",

    buttonText: "Check Eligibility Now",
  },
};

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Home Loan",

  description:
    "The exact documents required depend on your employment profile and loan purpose. Below is the standard document checklist used by Money Mart for most Home Loan applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "1 Selfie/Photograph (Applicant & Co-applicant)",
        "PAN Card (Applicant & Co-applicant)",
        "Latest Downloaded Aadhaar Card (Applicant & Co-applicant)",
      ],
    },

    {
      icon: Briefcase,
      title: "Additional Documents for Salaried Applicants",

      documents: [
        "Last 3 Months Salary Slips",
        "Last 2 Years Form 16",
        "Salary & Savings Account Statements for the Last 7 Months",
        "Existing Loan Details (Sanction Letter / Repayment Schedule), if applicable",
        "Complete Property Document Chain",
      ],
    },

    {
      icon: Building2,
      title: "Additional Documents for Self-Employed Applicants",

      documents: [
        "GST Registration Certificate",
        "UDYAM Registration Certificate",
        "Last 3 Years Income Tax Returns with Profit & Loss Statement & Balance Sheet",
        "Audit Report (if applicable)",
        "Current & Savings Account Statements for the Last 7 Months",
        "1 Year Banking Statements for Balance Transfer Cases",
        "Existing Loan Details, if applicable",
        "Complete Property Document Chain",
      ],
    },

    {
      icon: Home,
      title: "Additional Documents for Property Purchase",

      documents: [
        "Complete Chain of Property Documents",
        "Sale Agreement",
        "Seller's PAN Card",
        "Seller's Aadhaar Card",
        "Cancelled Cheque (Preferred) or Front Page of Passbook",
        "Seller's Contact Number",
      ],
    },

    {
      icon: Hammer,
      title: "Additional Documents for Self Construction",

      documents: [
        "Complete Chain of Property Documents",
        "Original Construction Estimate from a Registered Engineer",
        "Approved Building Map",
        "Construction Permission from the Concerned Authority",
      ],
    },

    {
      icon: RefreshCcw,
      title: "Additional Documents for Balance Transfer",

      documents: [
        "Complete Chain of Property Documents",
        "Copy of Existing Loan Sanction Letter",
        "Loan Statement (From Disbursement Till Date)",
        "List of Original Documents Held by Existing Lender",
        "Outstanding / Foreclosure Letter",
      ],
    },
  ],
};

export const loanProcess = {
  ...process,

  title: "Your Home Loan in 6 Simple Steps",

  description:
    "From your first enquiry to loan disbursement, our team stays with you at every stage to ensure a smooth and hassle-free experience.",
};

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Apply Through Money Mart Instead of Visiting Multiple Banks?",

  description:
    "Applying through Money Mart gives you access to multiple lending partners, experienced loan advisors, and personalized assistance throughout your home loan journey—all through a single point of contact.",

  items: whyMoneyMartItems,
};

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Home Loan FAQs",

  description:
    "Find answers to some of the most common questions about Home Loans. If you need more information, our loan experts are happy to help.",

  items: [
    {
      question: "Who can apply for a Home Loan?",
      answer:
        "Both salaried and self-employed individuals can apply for a home loan, subject to the lender's eligibility criteria, income, age, credit history, and repayment capacity.",
    },
    {
      question: "What is the minimum CIBIL score required?",
      answer:
        "Most lenders prefer a CIBIL score of 750 or above, though approval depends on multiple eligibility factors.",
    },
    {
      question: "How much Home Loan can I get?",
      answer:
        "The eligible amount depends on your income, repayment capacity, credit profile, property value, and lender policies.",
    },
    {
      question: "Can I transfer my existing Home Loan?",
      answer:
        "Yes. A balance transfer allows you to move your existing loan to another lender for better interest rates or repayment terms.",
    },
    {
      question: "How long does approval take?",
      answer:
        "Approval timelines vary by lender but are generally completed within a few working days after successful document verification.",
    },
    {
      question: "Are there any hidden charges?",
      answer:
        "No. All lender charges, processing fees, and applicable costs are explained transparently before you proceed.",
    },
  ],
};

export const cta = {
  badge: "Ready to Own Your Dream Home?",

  title: "Let's Find the Right Home Loan for You",

  description:
    "Compare offers from leading banks and NBFCs, receive expert guidance, and complete your home loan journey with confidence. Our advisors are here to help you every step of the way.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Home Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

export const leadForm = {
  badge: "Free Home Loan Consultation",

  title: "Speak to a Home Loan Expert",

  description:
    "Looking for the best Home Loan? Our advisors compare offers from multiple lenders, maximize your eligibility, and help you throughout the entire loan journey—from enquiry to disbursement.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-1L",
        label: "₹50,000 – ₹1,00,000",
      },
      {
        value: "1L-2L",
        label: "₹1,00,000 – ₹2,00,000",
      },
      {
        value: "2L-plus",
        label: "Above ₹2,00,000",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your loan enquiry. No spam. No hidden charges.",
  },
};

const homeLoan = {
  hero,

  overview,

  benefits,

  types,

  eligibility,

  documents,

  process: loanProcess,

  whyMoneyMart,

  faq,

  cta,

  leadForm,
};

export default homeLoan;
